import type { KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { ArrowDownIcon, CloseIcon, SpinnerIcon } from "@/assets/icons";
import { TextTransformType, transformText } from "@/helpers/string";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { useMenuPosition } from "@/hooks/ui/useMenuPosition";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import { DROPDOWN_HEIGHT_ITEM } from "../dropdownMultiSelect";
import type {
  DropdownActionButtonProps,
  DropdownDataItem,
  DropdownFloatingLabelProps,
  DropdownLabelProps,
  DropdownMenuProps,
  DropdownOptionsProps,
  DropdownProps,
  IOptionItem,
  OptionWithMatch
} from "./types";

const DropdownActionButton = ({
  withSearch,
  selectValue,
  setSelectValue,
  setSearchTerm,
  onSelect,
  isOpen,
  disabled,
  setIsOpen
}: DropdownActionButtonProps) => {
  if (withSearch && selectValue?.value) {
    const clearSelection = () => {
      setSelectValue(null);
      setSearchTerm("");
      onSelect(null);
    };
    const handleClearKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      event.stopPropagation();
      clearSelection();
    };

    return (
      <button
        aria-label="Clear selection"
        aria-disabled={disabled}
        className="inline-flex"
        tabIndex={disabled ? -1 : 0}
        onClick={(event) => {
          event.stopPropagation();
          if (disabled) return;
          clearSelection();
        }}
        onKeyDown={handleClearKeyDown}
      >
        <CloseIcon
          className={twMerge(
            "fill-dim-gray w-5 h-5",
            isOpen && "rotate-180",
            disabled && "fill-light-gray"
          )}
          aria-hidden="true"
          focusable="false"
        />
      </button>
    );
  }
  return (
    <ArrowDownIcon
      onClick={() => {
        setIsOpen(!isOpen);
      }}
      aria-label="Toggle dropdown"
      className={twMerge(
        "ml-2 fill-dim-gray",
        isOpen && "rotate-180",
        disabled && "fill-light-gray"
      )}
      aria-hidden="true"
      focusable="false"
    />
  );
};

const DropdownOptions = <T extends IOptionItem>({
  allowNoSelection,
  noSelectionLabel,
  handleSelectItem,
  filteredData,
  renderOption,
  textTransform,
  hasMore,
  isLoadingMore,
  onLoadMore
}: DropdownOptionsProps<T> & { onLoadMore?: () => void }) => (
  <div className="py-1" role="none">
    {allowNoSelection && (
      <button
        key="no-selection"
        type="button"
        className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 cursor-pointer"
        role="menuitem"
        onClick={() => handleSelectItem(null)}
      >
        {noSelectionLabel}
      </button>
    )}
    <InfiniteScroll
      dataLength={filteredData.length}
      next={onLoadMore ?? (() => {})}
      hasMore={hasMore}
      loader={null}
      scrollableTarget="dropdown-scrollable-menu"
      scrollThreshold="95%"
      className="flex flex-col"
    >
      {filteredData.map((item) => (
        <button
          key={item.value}
          type="button"
          className={twMerge(
            "block w-full px-4 py-2 text-sm text-left",
            item.disabled
              ? "text-gray-400 cursor-not-allowed"
              : [
                  "text-gray-700 hover:bg-gray-50 cursor-pointer",
                  item.isMatched && "bg-teal-50"
                ]
          )}
          role="menuitem"
          onClick={() => handleSelectItem(item)}
          disabled={item.disabled}
        >
          {renderOption ? (
            renderOption(item)
          ) : (
            <div className="flex flex-col">
              <span
                className={
                  item.description ? "font-semibold text-[12px] truncate" : ""
                }
                title={item.label}
              >
                {transformText(item.label, textTransform)}
              </span>
              {item.description && (
                <span className="text-[10px] truncate" title={item.description}>
                  {item.description}
                </span>
              )}
            </div>
          )}
        </button>
      ))}
      {isLoadingMore && (
        <div className="w-full py-2 text-center">
          <SpinnerIcon className="mx-auto spin-animation w-5 h-5" />
        </div>
      )}
    </InfiniteScroll>
  </div>
);

function getFilteredData<T extends IOptionItem>(
  data: DropdownDataItem<T>,
  withSearch: boolean,
  searchTerm: string,
  filterMatchedSearch?: boolean
): OptionWithMatch<T>[] {
  if (!withSearch) return data;

  const term = searchTerm.trim().toLowerCase();
  const isValidSearch = term.length >= 2;

  return filterMatchedSearch && term
    ? data.filter((item) => item.label.toLowerCase().includes(term))
    : data
        .map((item) => {
          const label = item.label.toLowerCase();
          const isMatched =
            isValidSearch &&
            (label.startsWith(term) ||
              label.split(" ").some((word) => word.startsWith(term)) ||
              label.includes(term));
          return {
            ...item,
            isMatched
          };
        })
        .sort((a, b) => Number(b.isMatched) - Number(a.isMatched));
}

function getSelectValueDisplay<T extends IOptionItem>(
  selectValue: T | null,
  renderSelectValue: ((option: T) => JSX.Element) | undefined,
  textTransform: TextTransformType,
  placeholder: string
) {
  if (selectValue) {
    return renderSelectValue
      ? renderSelectValue(selectValue)
      : transformText(selectValue.label, textTransform);
  }
  return placeholder;
}

const DropdownLabel = ({ label }: DropdownLabelProps) =>
  label ? <span className="text-xs font-bold">{label}</span> : null;

const DropdownFloatingLabel = ({
  selectValue,
  showLabelOnTop,
  labelBorder,
  isOpen,
  required = false
}: DropdownFloatingLabelProps) =>
  selectValue?.value && showLabelOnTop ? (
    <label
      className={twMerge(
        "pointer-events-none absolute left-2 top-[-10px] mb-0 max-w-[90%] truncate",
        "bg-white px-2 text-[10px] text-txtBody",
        isOpen && "text-teal-500"
      )}
    >
      {labelBorder}
      {required && <span className="text-cinnabar"> *</span>}
    </label>
  ) : null;

const DropdownMenu = <T extends IOptionItem>({
  isOpen,
  isAbsoluteView,
  showMenuFromTop,
  menuListClassName,
  allowNoSelection,
  noSelectionLabel,
  handleSelectItem,
  filteredData,
  renderOption,
  textTransform,
  hasMore,
  isLoadingMore,
  onLoadMore
}: DropdownMenuProps<T> & { onLoadMore?: () => void }) =>
  isOpen ? (
    <div
      id="dropdown-scrollable-menu"
      className={twMerge(
        "z-[999] w-full rounded-md bg-white ring-1 custom-scrollbar overflow-y-auto max-h-[300px]",
        "shadow-lg ring-black/5 focus:outline-hidden",
        isAbsoluteView && "absolute",
        showMenuFromTop ? "top-11" : "bottom-11",
        menuListClassName
      )}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
    >
      <DropdownOptions
        allowNoSelection={allowNoSelection}
        noSelectionLabel={noSelectionLabel}
        handleSelectItem={handleSelectItem}
        filteredData={filteredData}
        renderOption={renderOption}
        textTransform={textTransform}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </div>
  ) : null;

const Dropdown = <T extends IOptionItem>({
  name,
  label,
  className,
  menuListClassName,
  data = [],
  onSelect,
  placeholder = "Select value",
  labelBorder = placeholder,
  defaultValue = null,
  value = null,
  allowNoSelection = false,
  noSelectionLabel = "No selection",
  isAbsoluteView = true,
  isLoading = false,
  withSearch = false,
  textTransform = "none",
  renderOption,
  renderSelectValue,
  disabled = false,
  isValidationError = false,
  showLabelOnTop = true,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  visible = true,
  filterMatchedSearch = false,
  suffix,
  required = false
}: DropdownProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectValue, setSelectValue] = useState<T | null>(defaultValue);
  const [searchTerm, setSearchTerm] = useState("");

  const showMenuFromTop = useMenuPosition(
    isOpen,
    ref,
    data.length,
    DROPDOWN_HEIGHT_ITEM,
    300, // max height of the menu
    16 // padding for the menu
  );

  useOnClickOutside(ref, () => setIsOpen(false));

  const handleSelectItem = (item: T | null) => {
    if (item?.disabled) return;
    onSelect(item);
    setSelectValue(item);
    setSearchTerm(item ? item.label : "");
    setIsOpen(false);
  };

  const filteredData: OptionWithMatch<T>[] = getFilteredData(
    data,
    withSearch,
    searchTerm,
    filterMatchedSearch
  );

  // Effect to set the initial value when the component mounts and no longer loading
  useEffect(() => {
    if (!isLoading) {
      setSelectValue(value);
      setSearchTerm(value ? value.label : "");
    }
  }, [isLoading, value]);

  // Return null if not visible
  if (!visible) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2 relative",
        isValidationError && "text-cinnabar",
        className
      )}
      ref={ref}
    >
      <DropdownLabel label={label} />
      <div className="relative inline-block text-left">
        <Button
          disabled={disabled}
          id="menu-button"
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="dropdown-menu-list"
          aria-label={name ? `Dropdown menu: ${name}` : "Dropdown menu"}
          className={twMerge(
            "flex items-center text-left h-[40px] w-full border border-gainsboro bg-white py-2 px-3 text-txtHeading",
            "text-sm focus:outline-none",
            disabled && "bg-white-smoke",
            isValidationError && "border border-cinnabar",
            isOpen && "border-teal-500"
          )}
          onClick={() => {
            if (!withSearch) setIsOpen((prev) => !prev);
          }}
        >
          {withSearch ? (
            <input
              name={name}
              type="text"
              value={transformText(searchTerm, textTransform)}
              onChange={(e) => {
                const newSearchTerm = e.target.value;
                setSearchTerm(newSearchTerm);
                // Reset selection when search is cleared
                if (newSearchTerm === "" && selectValue) {
                  setSelectValue(null);
                  onSelect(null);
                }
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={selectValue ? "" : placeholder}
              className={twMerge(
                "flex-grow text-xs bg-transparent w-full border-none outline-none",
                searchTerm.trim() || selectValue
                  ? "text-txtHeading"
                  : "text-txtPlaceholder",
                disabled && "text-light-gray"
              )}
              aria-label={placeholder}
              disabled={disabled}
            />
          ) : (
            <span
              className={twMerge(
                "mr-auto text-xs w-full truncate",
                selectValue ? "text-txtHeading" : "text-txtPlaceholder",
                disabled && "text-light-gray"
              )}
              aria-live="polite"
              aria-atomic="true"
            >
              {getSelectValueDisplay(
                selectValue,
                renderSelectValue,
                textTransform,
                placeholder
              )}
            </span>
          )}
          {isLoading && (
            <SpinnerIcon
              className="ml-2 spin-animation"
              aria-label="Loading options"
            />
          )}
          <DropdownActionButton
            withSearch={withSearch}
            selectValue={selectValue}
            setSelectValue={(item) => setSelectValue(item as T | null)}
            setSearchTerm={setSearchTerm}
            onSelect={(item) => onSelect(item as T | null)}
            isOpen={isOpen}
            disabled={disabled}
            setIsOpen={setIsOpen}
          />
          {suffix}
        </Button>
        <DropdownFloatingLabel
          selectValue={selectValue}
          showLabelOnTop={showLabelOnTop}
          labelBorder={labelBorder}
          isOpen={isOpen}
          required={required}
        />
        <DropdownMenu
          isOpen={isOpen}
          isAbsoluteView={isAbsoluteView}
          showMenuFromTop={showMenuFromTop}
          menuListClassName={menuListClassName}
          allowNoSelection={allowNoSelection}
          noSelectionLabel={noSelectionLabel}
          handleSelectItem={handleSelectItem}
          filteredData={filteredData}
          renderOption={renderOption}
          textTransform={textTransform}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
        />
      </div>
    </div>
  );
};

export default Dropdown;
